// @file Config bundle and picker state types for the content picker
import { ContentPickerSource } from '@@/bits/content_picker'
import type { UploadPurpose } from '@@/bits/uploader'

// #region PickerConfig
interface AudioRecorderPickerConfig {
  audioLengthLimit?: number
  /** URL to open in a new window when mic permissions fail (e.g. in LTI/Teams iframes). */
  retryInNewWindowUrl?: string
  handlers: {
    onSave: (file: File, options: { uploadPurpose: UploadPurpose; recordingDuration: number }) => void
  }
}

interface VideoRecorderPickerConfig {
  videoLengthLimit?: number
  /** API token for Snap Camera Kit integration. When provided, enables AR lenses. */
  cameraKitApiToken?: string
  /** Whether AR lenses are enabled. AR lenses also require cameraKitApiToken. */
  arLensesEnabled?: boolean
  /** URL to open in a new window when camera/mic permissions fail (e.g. in LTI/Teams iframes). */
  retryInNewWindowUrl?: string
  handlers: {
    onSave: (
      file: File,
      options: {
        trimVideoAfterUpload?: { start: number; end: number }
        uploadPurpose: 'video_recording'
        recordingDuration: number
      },
    ) => void
    onOpenVideoLengthLimitSettings?: () => void
  }
}

interface PhotoBoothPickerConfig {
  /** URL to open in a new window when camera permissions fail (e.g. in LTI/Teams iframes). */
  retryInNewWindowUrl?: string
  handlers: {
    onSave: (file: File) => void
  }
}

interface ScreenRecorderPickerConfig {
  screenRecordingLengthLimit?: number
  retryInNewWindowUrl?: string
  handlers: {
    onShow?: () => void
    onSave: (
      file: File,
      options: {
        trimVideoAfterUpload?: { start: number; end: number }
        uploadPurpose: 'video_recording'
        recordingDuration: number
      },
    ) => void
    onClose?: () => void
    onOpenScreenRecordingLengthLimitSettings?: () => void
  }
}

type PickerConfigMap = {
  [ContentPickerSource.AudioRecorder]?: AudioRecorderPickerConfig
  [ContentPickerSource.VideoRecorder]?: VideoRecorderPickerConfig
  [ContentPickerSource.PhotoBooth]?: PhotoBoothPickerConfig
  [ContentPickerSource.ScreenshareRecorder]?: ScreenRecorderPickerConfig
}

// #endregion PickerConfig

// #region PickerStatus

enum ScreenRecorderStatus {
  Hidden = 'Hidden',
  Connecting = 'Connecting',
  Streaming = 'Streaming',
  Recording = 'Recording',
  Paused = 'Paused',
  Previewing = 'Previewing',
  Saving = 'Saving',
  Submitted = 'Submitted',
}

/**
 * @enum VideoRecorderStatus
 * @description The status of the video recorder
 */
enum VideoRecorderStatus {
  Connecting = 'Connecting',
  Errored = 'Errored',
  Streaming = 'Streaming',
  Recording = 'Recording',
  Paused = 'Paused',
  Previewing = 'Previewing',
  Saving = 'Saving',
  Submitted = 'Submitted',
}

enum AudioRecorderStatus {
  Connecting = 'Connecting',
  Errored = 'Errored',
  Streaming = 'Streaming',
  Recording = 'Recording',
  Paused = 'Paused',
  Previewing = 'Previewing',
}

enum PhotoBoothStatus {
  Connecting = 'Connecting',
  Errored = 'Errored',
  Streaming = 'Streaming',
  Capturing = 'Capturing',
  Captured = 'Captured',
}

// #endregion PickerStatus

// #region PickerState

interface PhotoBoothPickerState {
  source: ContentPickerSource.PhotoBooth
  photoBoothStatus: PhotoBoothStatus
}

interface VideoRecorderPickerState {
  source: ContentPickerSource.VideoRecorder
  videoRecorderStatus: VideoRecorderStatus
}

interface ScreenRecorderPickerState {
  source: ContentPickerSource.ScreenshareRecorder
  screenRecorderStatus: ScreenRecorderStatus
  returnToUrl: string | null
}

interface AudioRecorderPickerState {
  source: ContentPickerSource.AudioRecorder
  audioRecorderStatus: AudioRecorderStatus
}

type PickerSourceState =
  | PhotoBoothPickerState
  | VideoRecorderPickerState
  | ScreenRecorderPickerState
  | AudioRecorderPickerState

// #endregion PickerState

// #region Session
interface ContentPickerSession {
  id: string
  enabledSources: ContentPickerSource[]
  activeComponent: ContentPickerSource | null
  pickerConfigs: PickerConfigMap
  pickerStates: Partial<Record<ContentPickerSource, PickerSourceState>>
}

// #endregion Session

export { AudioRecorderStatus, PhotoBoothStatus, ScreenRecorderStatus, VideoRecorderStatus }
export type {
  AudioRecorderPickerConfig,
  AudioRecorderPickerState,
  ContentPickerSession,
  PhotoBoothPickerConfig,
  PhotoBoothPickerState,
  PickerConfigMap,
  PickerSourceState,
  ScreenRecorderPickerConfig,
  ScreenRecorderPickerState,
  VideoRecorderPickerConfig,
  VideoRecorderPickerState,
}
